/**
 * Utility functions to download and convert images for Word export
 */

import { getStorage, ref, getBlob } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import app from '../firebase/config';

/**
 * Checks if a URL is a Firebase Storage URL
 */
function isFirebaseStorageUrl(url: string): boolean {
  return url.includes('firebasestorage.googleapis.com') || url.includes('firebase.storage');
}

/**
 * Extracts the storage path from a Firebase Storage download URL
 * Example: https://firebasestorage.googleapis.com/v0/b/bucket/o/images%2Ffile.png?alt=media&token=xxx
 * Returns: images/file.png
 */
function extractStoragePathFromUrl(url: string): string | null {
  try {
    const urlObj = new URL(url);
    // The path is in the format: /v0/b/{bucket}/o/{path}
    // Handle both with and without query parameters
    const pathMatch = urlObj.pathname.match(/\/o\/(.+)$/);
    if (pathMatch) {
      // Decode the URL-encoded path
      const decodedPath = decodeURIComponent(pathMatch[1]);
      // Remove query parameters if they were included in the path
      const cleanPath = decodedPath.split('?')[0];
      return cleanPath;
    }
    return null;
  } catch (error) {
    console.warn('Failed to extract storage path from URL:', error);
    return null;
  }
}

/**
 * Downloads an image from a URL and converts it to a buffer
 * @param imageUrl The URL of the image
 * @returns A Promise that resolves to an ArrayBuffer, or null if download fails
 */
export async function downloadImageAsBuffer(imageUrl: string): Promise<ArrayBuffer | null> {
  try {
    // Handle data URLs
    if (imageUrl.startsWith('data:')) {
      const base64Data = imageUrl.split(',')[1];
      if (!base64Data) return null;
      
      const binaryString = atob(base64Data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      return bytes.buffer;
    }
    
    // Handle Firebase Storage URLs with authentication
    if (isFirebaseStorageUrl(imageUrl)) {
      if (!app) {
        console.warn('Firebase app not initialized, cannot download image:', imageUrl);
        return null;
      }
      
      // Check if user is authenticated (required for Firebase Storage)
      try {
        const auth = getAuth(app);
        const currentUser = auth.currentUser;
        if (!currentUser) {
          console.warn('User not authenticated, cannot download image from Firebase Storage:', imageUrl);
          return null;
        }
      } catch (authError) {
        console.warn('Failed to check authentication:', authError);
        return null;
      }
      
      try {
        const storagePath = extractStoragePathFromUrl(imageUrl);
        if (!storagePath) {
          console.warn(`Failed to extract storage path from URL: ${imageUrl}`);
          return null;
        }
        
        console.log(`Downloading image from Firebase Storage: ${storagePath}`);
        const storage = getStorage(app);
        const storageRef = ref(storage, storagePath);
        
        // Use getBlob which handles authentication automatically
        // getBlob returns a Blob that we can convert to ArrayBuffer
        const blob = await getBlob(storageRef);
        const arrayBuffer = await blob.arrayBuffer();
        console.log(`Successfully downloaded image: ${storagePath} (${arrayBuffer.byteLength} bytes)`);
        return arrayBuffer;
      } catch (firebaseError: any) {
        console.error(`Failed to download image from Firebase Storage: ${imageUrl}`, firebaseError);
        
        // Provide more specific error information
        if (firebaseError?.code === 'storage/unauthorized') {
          console.error('Storage access denied. Check Firebase Storage security rules.');
        } else if (firebaseError?.code === 'storage/object-not-found') {
          console.error('Image not found in Firebase Storage.');
        } else if (firebaseError?.code === 'storage/retry-limit-exceeded') {
          console.error(
            '⚠️ CORS Configuration Required!\n' +
            'Firebase Storage bucket needs CORS configuration to download images.\n' +
            'Please configure CORS following instructions in docs/CORS_SETUP_FOR_EXPORT.md\n' +
            'Or run: gsutil cors set cors.json gs://slam-2fe64.firebasestorage.app'
          );
        }
        
        // Don't fallback to fetch() for Firebase URLs - it will cause CORS issues
        // Return null instead
        return null;
      }
    }
    
    // Handle regular URLs (only for non-Firebase URLs)
    // Note: Firebase URLs should never reach here - they return null above if download fails
    console.log(`Downloading non-Firebase image: ${imageUrl}`);
    try {
      const response = await fetch(imageUrl, {
        mode: 'cors',
        credentials: 'omit'
      });
      
      if (!response.ok) {
        console.warn(`Failed to download image: ${imageUrl}`, response.statusText);
        return null;
      }
      
      const blob = await response.blob();
      return await blob.arrayBuffer();
    } catch (fetchError) {
      console.warn(`Error fetching image: ${imageUrl}`, fetchError);
      return null;
    }
  } catch (error) {
    console.warn(`Error downloading image: ${imageUrl}`, error);
    return null;
  }
}

/**
 * Resizes and compresses an image to reduce file size
 * @param imageBuffer The image as ArrayBuffer
 * @param maxWidth Maximum width in pixels (default: 1200)
 * @param maxHeight Maximum height in pixels (default: 1200)
 * @param quality JPEG quality 0-1 (default: 0.85, only used for JPEG)
 * @returns A Promise that resolves to an object with resized ArrayBuffer and dimensions, or null if resize fails
 */
export async function resizeAndCompressImage(
  imageBuffer: ArrayBuffer,
  maxWidth: number = 1200,
  maxHeight: number = 1200,
  quality: number = 0.85
): Promise<{ buffer: ArrayBuffer; width: number; height: number } | null> {
  try {
    // Create a blob from the buffer to detect MIME type
    const blob = new Blob([imageBuffer]);
    const imageUrl = URL.createObjectURL(blob);
    
    // Load image into canvas
    const img = new Image();
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = imageUrl;
    });
    
    // Calculate new dimensions while maintaining aspect ratio
    let { width, height } = img;
    const needsResize = width > maxWidth || height > maxHeight;
    
    if (!needsResize) {
      // Image is already small enough, return original with dimensions
      URL.revokeObjectURL(imageUrl);
      return { buffer: imageBuffer, width: img.width, height: img.height };
    }
    
    const ratio = Math.min(maxWidth / width, maxHeight / height);
    width = Math.round(width * ratio);
    height = Math.round(height * ratio);
    
    // Create canvas and draw resized image
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      URL.revokeObjectURL(imageUrl);
      return null;
    }
    
    // Use high-quality image rendering
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    
    // Detect original format from blob type or use PNG as safe default
    // PNG is safer for Word documents as it preserves transparency and is well-supported
    const mimeType = blob.type || 'image/png';
    const outputFormat = mimeType.includes('jpeg') || mimeType.includes('jpg') 
      ? 'image/jpeg' 
      : 'image/png';
    
    // Fill white background for JPEG conversion (prevents black background on transparent PNGs)
    if (outputFormat === 'image/jpeg') {
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, width, height);
    }
    
    // Convert to blob with compression
    const compressedBlob = await new Promise<Blob | null>((resolve) => {
      if (outputFormat === 'image/jpeg') {
        canvas.toBlob(
          (blob) => resolve(blob),
          'image/jpeg',
          quality
        );
      } else {
        // PNG - no quality parameter needed
        canvas.toBlob(
          (blob) => resolve(blob),
          'image/png'
        );
      }
    });
    
    // Clean up
    URL.revokeObjectURL(imageUrl);
    
    if (!compressedBlob) {
      return null;
    }
    
    // Convert blob to ArrayBuffer
    const compressedBuffer = await compressedBlob.arrayBuffer();
    
    console.log(
      `Image resized: ${img.width}x${img.height} -> ${width}x${height}, ` +
      `format: ${outputFormat}, ` +
      `size: ${(imageBuffer.byteLength / 1024).toFixed(1)}KB -> ${(compressedBuffer.byteLength / 1024).toFixed(1)}KB`
    );
    
    return { buffer: compressedBuffer, width, height };
  } catch (error) {
    console.warn('Failed to resize image:', error);
    // Try to get dimensions from original image before returning
    try {
      const blob = new Blob([imageBuffer]);
      const imageUrl = URL.createObjectURL(blob);
      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = imageUrl;
      });
      URL.revokeObjectURL(imageUrl);
      return { buffer: imageBuffer, width: img.width, height: img.height };
    } catch {
      // If we can't get dimensions, return null to skip this image
      return null;
    }
  }
}

/**
 * Gets the image format from a URL or data URL
 * @param imageUrl The URL of the image
 * @returns The image format (png, jpeg, gif, etc.) or 'png' as default
 */
export function getImageFormat(imageUrl: string): 'png' | 'jpeg' | 'gif' | 'bmp' | 'svg+xml' {
  if (imageUrl.startsWith('data:')) {
    const mimeMatch = imageUrl.match(/data:image\/([^;]+)/);
    if (mimeMatch) {
      const format = mimeMatch[1].toLowerCase();
      if (format === 'jpg' || format === 'jpeg') return 'jpeg';
      if (format === 'png') return 'png';
      if (format === 'gif') return 'gif';
      if (format === 'bmp') return 'bmp';
      if (format === 'svg+xml') return 'svg+xml';
    }
    return 'png'; // Default for data URLs
  }
  
  // Extract extension from URL
  const urlLower = imageUrl.toLowerCase();
  if (urlLower.includes('.jpg') || urlLower.includes('.jpeg')) return 'jpeg';
  if (urlLower.includes('.png')) return 'png';
  if (urlLower.includes('.gif')) return 'gif';
  if (urlLower.includes('.bmp')) return 'bmp';
  if (urlLower.includes('.svg')) return 'svg+xml';
  
  return 'png'; // Default
}
